import { InstituteNumbers } from '@/components/common/InstituteNumbers';
import { PersonDetailLines } from '@/components/common/PersonDetailLines';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { CODE_BADGE_CLASS } from '@/styles/codeBadge';
import { joinAreaNames } from '@/utils/areaNames';
import { formatDisplayDate, formatTime } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type { MpApprovalRow } from '../types/marketPromotion.types';
import { DecisionStatusBadge } from './DecisionStatusBadge';
import { RecordSummaryPanel, type SummaryField } from './RecordSummaryPanel';

const DETAIL_LINE_CLASS = 'block text-xs font-normal text-muted-foreground';

function getSummaryFields(isMisQueue: boolean): SummaryField<MpApprovalRow>[] {
  return [
    {
      label: 'ZM',
      value: (donation) =>
        donation.enteredByName ? (
          <>
            <PersonDetailLines
              name={donation.enteredByName}
              mobile={isMisQueue ? donation.enteredByMobile : donation.enteredByContact}
            />
            <span className={DETAIL_LINE_CLASS}>
              {joinAreaNames([donation.enteredByThana, donation.enteredByDistrict])}
            </span>
          </>
        ) : null,
    },
    {
      label: 'Institute',
      value: (donation) =>
        donation.instituteName ? (
          <>
            <span className="block">{donation.instituteName}</span>
            <span className={cn(DETAIL_LINE_CLASS, 'mt-1')}>
              <InstituteNumbers code={donation.instituteCode} eiin={donation.instituteEiin} />
            </span>
          </>
        ) : null,
    },
    {
      label: 'Entered',
      value: (donation) =>
        donation.enteredAt ? (
          <>
            <span className="block">{formatDisplayDate(donation.enteredAt)}</span>
            <span className={DETAIL_LINE_CLASS}>{formatTime(donation.enteredAt)}</span>
          </>
        ) : null,
    },
    { label: 'Paperwork', value: (donation) => (donation.hasAttachment ? 'On file' : 'None') },
  ];
}

function getDonationBadges(donation: MpApprovalRow) {
  return (
    <>
      {donation.promoItemNo === null ? null : (
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Project Code
          <span className={CODE_BADGE_CLASS}>{donation.promoItemNo}</span>
        </span>
      )}
      <DecisionStatusBadge approveTypeNo={donation.approveTypeNo} />
    </>
  );
}

interface DonationSummaryPanelProps {
  donation: MpApprovalRow | undefined;
  isMisQueue: boolean;
}

export function DonationSummaryPanel({ donation, isMisQueue }: DonationSummaryPanelProps) {
  return (
    <RecordSummaryPanel
      ariaLabel="Donation summary"
      record={donation}
      badges={getDonationBadges}
      title={(record) => record.itemName}
      amountLabel="Donation Amount"
      amount={(record) => record.approvedAmount}
      amountNote={(record) => `Claimed ${TAKA_SYMBOL} ${formatNumber(record.claimedAmount)}`}
      fields={getSummaryFields(isMisQueue)}
    />
  );
}
